import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { BUSINESS_BASE_DOMAIN } from "@/lib/constants";

interface SandboxContextType {
  sandboxMode: boolean;
  setSandboxMode: (value: boolean) => void;
  isProductionDomain: boolean;
}

const SandboxContext = createContext<SandboxContextType | undefined>(undefined);

const STORAGE_KEY = "bp_sandbox_mode";

const PRODUCTION_DOMAINS = [BUSINESS_BASE_DOMAIN, "businesspal-ie.lovable.app"];

function checkIsProductionDomain(): boolean {
  try {
    const host = window.location.hostname;
    return PRODUCTION_DOMAINS.some(d => host === d || host.endsWith(`.${d}`));
  } catch {
    return false;
  }
}

export function SandboxProvider({ children }: { children: ReactNode }) {
  const isProd = checkIsProductionDomain();

  const [sandboxMode, setSandboxModeState] = useState(() => {
    if (isProd) {
      // Force clear any stale sandbox state on production
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      return false;
    }
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Safety net: continuously enforce sandbox=false on production domains
  useEffect(() => {
    if (isProd && sandboxMode) {
      setSandboxModeState(false);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    }
  }, [isProd, sandboxMode]);

  const setSandboxMode = (value: boolean) => {
    // Never allow sandbox on production domains
    if (isProd) return;
    setSandboxModeState(value);
    try {
      if (value) {
        localStorage.setItem(STORAGE_KEY, "true");
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* ignore */ }
  };

  return (
    <SandboxContext.Provider value={{ sandboxMode, setSandboxMode, isProductionDomain: isProd }}>
      {children}
    </SandboxContext.Provider>
  );
}

export function useSandbox() {
  const context = useContext(SandboxContext);
  if (!context) {
    throw new Error("useSandbox must be used within a SandboxProvider");
  }
  return context;
}
